import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { isValidHttpUrl } from "@/lib/refclick-utils";

/**
 * Add / Edit link modal.
 * All validation happens inline (no toasts for field errors) so the user
 * sees exactly what's wrong right next to the offending field.
 */
export default function LinkModal({ open, mode, link, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [target, setTarget] = useState(5);
  // Phase 5: per-link $/click rate. Default 25 for new links; the
  // detail-panel already exposes this too, but we mirror it here so
  // the user can tweak it at creation time.
  const [rate, setRate] = useState(25);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset the form whenever the modal opens (or the "link" it edits changes)
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && link) {
      setName(link.name || "");
      setUrl(link.url || "");
      setTarget(link.daily_target || 5);
      setRate(
        link.usd_per_click === 0 || link.usd_per_click ? link.usd_per_click : 25,
      );
    } else {
      setName("");
      setUrl("");
      setTarget(5);
      setRate(25);
    }
    setErrors({});
    setSubmitting(false);
  }, [open, mode, link]);

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Give this link a name.";
    if (!url.trim()) next.url = "URL is required.";
    else if (!isValidHttpUrl(url.trim())) next.url = "Must be a valid http:// or https:// URL.";
    const t = Number(target);
    if (!Number.isInteger(t) || t < 1) next.target = "Target must be a whole number ≥ 1.";
    const r = Number(rate);
    if (!Number.isFinite(r) || r < 0 || r > 10000) {
      next.rate = "$ per click must be between 0 and 10000.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      await onSubmit({
        name: name.trim(),
        url: url.trim(),
        daily_target: Number(target),
        usd_per_click: Number(rate),
      });
    } catch {
      // Server-side validation errors are surfaced via toast in Dashboard.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="link-modal"
        className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#101012] text-text-main sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">
            {mode === "edit" ? "Edit link" : "Add a new link"}
          </DialogTitle>
          <DialogDescription className="text-text-muted">
            {mode === "edit"
              ? "Change the name, URL or daily target."
              : "Track how many times you click a referral link each day."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rl-name" className="text-xs uppercase tracking-widest text-text-muted">
              Name
            </Label>
            <Input
              id="rl-name"
              data-testid="link-modal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Notion referral"
              className="border-white/10 bg-black/30 text-text-main placeholder:text-text-muted/60 focus-visible:border-accent focus-visible:ring-accent/50"
              maxLength={80}
              autoFocus
            />
            {errors.name && (
              <span className="font-mono-safe text-xs text-red-400" data-testid="err-name">
                {errors.name}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rl-url" className="text-xs uppercase tracking-widest text-text-muted">
              URL
            </Label>
            <Input
              id="rl-url"
              data-testid="link-modal-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/?ref=you"
              className="font-mono-safe border-white/10 bg-black/30 text-sm text-text-main placeholder:text-text-muted/60 focus-visible:border-accent focus-visible:ring-accent/50"
              inputMode="url"
            />
            {errors.url && (
              <span className="font-mono-safe text-xs text-red-400" data-testid="err-url">
                {errors.url}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="rl-target"
              className="text-xs uppercase tracking-widest text-text-muted"
            >
              Daily target
            </Label>
            <Input
              id="rl-target"
              data-testid="link-modal-target"
              type="number"
              min={1}
              max={999}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-28 border-white/10 bg-black/30 font-mono-num text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
            />
            {errors.target && (
              <span className="font-mono-safe text-xs text-red-400" data-testid="err-target">
                {errors.target}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="rl-rate"
              className="text-xs uppercase tracking-widest text-text-muted"
            >
              $ per click
            </Label>
            <div className="relative w-28">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 font-mono-num text-xs text-text-muted/70">
                $
              </span>
              <Input
                id="rl-rate"
                data-testid="link-modal-rate"
                type="number"
                min={0}
                max={10000}
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="border-white/10 bg-black/30 pl-5 font-mono-num text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
              />
            </div>
            <span className="font-mono-safe text-[11px] leading-snug text-text-muted/80">
              Auto-added to earnings on every +1. Set 0 to disable.
            </span>
            {errors.rate && (
              <span className="font-mono-safe text-xs text-red-400" data-testid="err-rate">
                {errors.rate}
              </span>
            )}
          </div>

          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-text-muted hover:bg-white/5 hover:text-text-main"
              data-testid="link-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="link-modal-submit"
              className="border border-accent/40 bg-accent/20 text-accent hover:bg-accent/30"
            >
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Add link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
